import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import { SUPERVISOR_DASHBOARD_QUERY_KEY } from './use-supervisor-dashboard';

import type { SetWidgetPreferencePayload, WidgetLayoutEntry } from '../types';
import type { UseMutationResult } from '@tanstack/react-query';

export const SUPERVISOR_WIDGETS_QUERY_KEY = ['supervisor-dashboard', 'widgets'] as const;

export function useSupervisorWidgetLayout(): {
  data: WidgetLayoutEntry[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: SUPERVISOR_WIDGETS_QUERY_KEY,
    queryFn: async () => (await api.getSupervisorWidgetLayout()).widgets,
    staleTime: 60 * 1000,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useSetWidgetPreference(): UseMutationResult<
  WidgetLayoutEntry,
  Error,
  { widgetId: string; body: SetWidgetPreferencePayload }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ widgetId, body }) => api.setWidgetPreference(widgetId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SUPERVISOR_WIDGETS_QUERY_KEY] });
    },
  });
}

export function useResetSupervisorWidgets(): UseMutationResult<
  { rowsRemoved: number },
  Error,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.resetSupervisorWidgets(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SUPERVISOR_WIDGETS_QUERY_KEY] });
    },
  });
}

export function useRefreshDashboard(): UseMutationResult<
  { accepted: true; computedAt: string },
  Error,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.refreshDashboard(),
    onSuccess: () => {
      // O backend recompõe e persiste o snapshot de forma síncrona antes do 202;
      // invalidar a state query força o refetch do /state recém-recomputado.
      void qc.invalidateQueries({ queryKey: [...SUPERVISOR_DASHBOARD_QUERY_KEY] });
    },
  });
}
